export interface FileCoordinates {
  project: string;
  path: string;
  ref: string;
}

const SOURCE_LOCATION = 'backstage.io/source-location';
const DEFAULT_REF = 'main';

interface Entity {
  metadata?: { annotations?: Record<string, unknown> };
  spec?: { mesh?: { specific?: { sourcePath?: unknown } } };
}

/**
 * Reads `url:https://host/group/sub/repo/-/blob/<ref>/...`, which is how the
 * catalog records where an entity came from. A ref containing slashes cannot be
 * told apart from the path that follows it, so the first segment wins and the UI
 * lets the user correct it.
 */
function fromSourceLocation(value: unknown): Partial<FileCoordinates> {
  if (typeof value !== 'string') {
    return {};
  }

  let url: URL;

  try {
    url = new URL(value.replace(/^url:/, ''));
  } catch {
    return {};
  }

  const [projectPath, remainder] = url.pathname.split(/\/-\/(?:blob|tree)\//);
  const project = projectPath.replace(/^\/|\/$/g, '');

  if (!project) {
    return {};
  }

  const ref = remainder?.split('/')[0];

  return ref ? { project, ref } : { project };
}

/**
 * Coordinates come from the catalog entity, but the UI lets the user override
 * them, so a partial or missing entity is a normal state rather than an error.
 */
export function coordinatesFromEntity(entity: unknown): Partial<FileCoordinates> {
  const typed = entity as Entity | undefined;
  const sourcePath = typed?.spec?.mesh?.specific?.sourcePath;

  return {
    ref: DEFAULT_REF,
    ...fromSourceLocation(typed?.metadata?.annotations?.[SOURCE_LOCATION]),
    ...(typeof sourcePath === 'string' && sourcePath.trim() ? { path: sourcePath.trim() } : {}),
  };
}

export function isComplete(coordinates: Partial<FileCoordinates>): coordinates is FileCoordinates {
  return Boolean(coordinates.project && coordinates.path && coordinates.ref);
}
